<script setup>
/**
 * Informes › Métricas Avanzadas / Rendimiento Digital (Victor 2026-05-23).
 * 4 pestañas: Dashboard Ejecutivo (datos reales) · Por Plataforma ·
 * Eficiencia del Equipo · Tiempo Real. Estilo M1.B Glass Negro Premium.
 * SOLO LECTURA. No afecta otros módulos.
 */
import { ref, computed, onMounted, onUnmounted } from 'vue';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import subDays from 'date-fns/subDays';
import startOfWeek from 'date-fns/startOfWeek';
import startOfMonth from 'date-fns/startOfMonth';
import format from 'date-fns/format';
import AdvancedMetricsAPI from 'dashboard/api/advancedMetrics';
import AgentDossierModal from './components/AgentDossierModal.vue';

// Expediente del agente (Victor 2026-06-06): clic en una fila de Eficiencia del
// Equipo abre el modal con a quién atiende ahora + lo que respondió.
const dossier = ref({ show: false, id: null, name: '', metric: null, metricLabel: '' });
// Clic en una métrica de la tabla → modal con SOLO los clientes de esa métrica
// (Victor 2026-06-07). El modal permite ver el chat y responderle al cliente.
const openMetric = (a, metric, label) => {
  dossier.value = { show: true, id: a.id, name: a.name, metric, metricLabel: label };
};
const openAgent = a => {
  dossier.value = { show: true, id: a.id, name: a.name, metric: null, metricLabel: '' };
};
const closeDossier = () => {
  dossier.value.show = false;
};

const tab = ref('ejec');
const loading = ref(true);
const exec = ref(null);
const branchFilter = ref(null); // null = todas

// ── Selector de período (Victor 2026-05-26): Hoy · Ayer · Esta semana ·
// Este mes · Rango de fecha. Recalcula KPIs + tabla con el mismo since/until
// para que TODO cuadre (consistencia). El backend ya acepta since/until (unix).
const period = ref('today'); // Hoy por defecto (Victor 2026-06-07), cambiable
const fromStr = ref(format(subDays(new Date(), 6), 'yyyy-MM-dd'));
const toStr = ref(format(new Date(), 'yyyy-MM-dd'));

const periodOptions = [
  { id: 'today', icon: '☀️', label: 'Hoy' },
  { id: 'yesterday', icon: '🌙', label: 'Ayer' },
  { id: 'week', icon: '📆', label: 'Esta semana' },
  { id: 'month', icon: '🗓️', label: 'Este mes' },
  { id: 'range', icon: '📍', label: 'Rango de fecha' },
];

const dateRange = computed(() => {
  const now = new Date();
  switch (period.value) {
    case 'today':
      return [startOfDay(now), now];
    case 'yesterday': {
      const y = subDays(now, 1);
      return [startOfDay(y), endOfDay(y)];
    }
    case 'month':
      return [startOfMonth(now), now];
    case 'range':
      return [startOfDay(new Date(`${fromStr.value}T00:00:00`)), endOfDay(new Date(`${toStr.value}T00:00:00`))];
    case 'week':
    default:
      return [startOfWeek(now, { weekStartsOn: 1 }), now];
  }
});

const rangeLabel = computed(() => {
  const [a, b] = dateRange.value;
  const f = d => format(d, 'dd MMM');
  return `${f(a)} – ${f(b)} ${format(b, 'yyyy')}`;
});

const baseParams = () => {
  const [a, b] = dateRange.value;
  const p = {
    since: Math.floor(a.getTime() / 1000),
    until: Math.floor(b.getTime() / 1000),
  };
  if (branchFilter.value) p.branch_id = branchFilter.value;
  return p;
};

// Pestaña 2 · Plataformas
const platLoading = ref(false);
const platData = ref(null);

// Pestaña 3 · Equipo
const teamLoading = ref(false);
const teamData = ref(null);

// Eficiencia · Inicio de sesión por sucursal (Victor 2026-06-09)
const teamMode = ref('performance'); // 'performance' | 'login'
const loginLoading = ref(false);
const loginData = ref(null);
const fetchTeamLogin = async () => {
  loginLoading.value = true;
  try {
    const { data } = await AdvancedMetricsAPI.teamLogin(baseParams());
    loginData.value = data;
  } catch (e) {
    loginData.value = null;
  } finally {
    loginLoading.value = false;
  }
};
const loginBranches = computed(() => loginData.value?.branches || []);
const loginSummary = computed(() => loginData.value?.summary || {});
const setTeamMode = m => {
  teamMode.value = m;
  if (m === 'login' && !loginData.value) fetchTeamLogin();
};
const statusLabel = a => {
  if (!a) return '';
  if (a.status === 'missing') return 'Sin iniciar';
  if (a.status === 'ontime') return 'A tiempo';
  if (a.multi) return `Tarde (${a.late_days} ${a.late_days === 1 ? 'día' : 'días'})`;
  return `Tarde +${a.delta_min} min`;
};

// Pestaña 4 · Tiempo Real
const rtData = ref(null);
let rtTimer = null;

// Pestaña 5 · Clientes en Vivo por Sucursal (Victor 2026-06-03)
const liveData = ref(null);
let liveTimer = null;
const liveSeg = ref('todos'); // todos | urgentes | michat
const liveDay = ref('hoy'); // hoy | ayer

const tabs = [
  { id: 'ejec', icon: '🏛', label: 'Dashboard Ejecutivo' },
  { id: 'plat', icon: '📡', label: 'Por Plataforma' },
  { id: 'team', icon: '👥', label: 'Eficiencia del Equipo' },
  { id: 'rt', icon: '⚡', label: 'Tiempo Real' },
  { id: 'live', icon: '🟢', label: 'Clientes en Vivo' },
];

const segLabel = { todos: 'Todos', urgentes: 'Urgentes', michat: 'Mi Chat' };

const branches = computed(() => exec.value?.branches || []);

// Menú de Sucursal plegado (Propuesta 1 · Victor 2026-06-07): un solo botón en
// lugar de 4 chips sueltos. branchLabel muestra el código seleccionado o "Todas".
const branchMenuOpen = ref(false);
const branchLabel = computed(() => {
  if (!branchFilter.value) return 'Todas';
  const b = branches.value.find(x => x.id === branchFilter.value);
  return b ? b.code : 'Todas';
});
const toggleBranchMenu = () => { branchMenuOpen.value = !branchMenuOpen.value; };
const pickBranch = id => {
  branchFilter.value = id;
  branchMenuOpen.value = false;
  applyFilters();
};

const agents = computed(() => exec.value?.agents || []);
const kpis = computed(() => exec.value?.kpis || {});

const fetchExecutive = async () => {
  loading.value = true;
  try {
    const { data } = await AdvancedMetricsAPI.executive(baseParams());
    exec.value = data;
  } catch (e) {
    exec.value = null;
  } finally {
    loading.value = false;
  }
};

const fetchTeam = async (silent = false) => {
  if (!silent) teamLoading.value = true;
  try {
    const { data } = await AdvancedMetricsAPI.team(baseParams());
    teamData.value = data;
  } catch (e) {
    if (!silent) teamData.value = null;
  } finally {
    if (!silent) teamLoading.value = false;
  }
};

// Refresco silencioso de presencia (Victor 2026-06-09): el punto verde/ocupado
// debe verse "en vivo" mientras se mira el equipo. Cada 20s sin spinner.
let teamTimer = null;
const stopTeamPolling = () => {
  if (teamTimer) clearInterval(teamTimer);
  teamTimer = null;
};
const startTeamPolling = () => {
  stopTeamPolling();
  teamTimer = setInterval(() => {
    if (teamMode.value === 'performance') fetchTeam(true);
  }, 20000);
};

const teamAgents = computed(() => teamData.value?.agents || []);
const teamBranches = computed(() => teamData.value?.branches || []);
const highlights = computed(() => teamData.value?.highlights || {});
const teamSummary = computed(() => teamData.value?.summary || {});

// Acordeón por sucursal (Victor 2026-06-09): todas cerradas por defecto; clic en
// la barra de cada sucursal la despliega/contrae.
const openBranches = ref(new Set());
const isBranchOpen = id => openBranches.value.has(id);
const toggleBranch = id => {
  const s = new Set(openBranches.value);
  if (s.has(id)) s.delete(id);
  else s.add(id);
  openBranches.value = s;
};

// Totales del día para la franja de la pestaña Equipo (Victor 2026-06-07):
// dinero del equipo, ventas (reservas), mejor vendedor y respuesta más rápida.
const teamTotals = computed(() => {
  const ags = teamAgents.value || [];
  const dinero = ags.reduce((s, a) => s + (a.dinero || 0), 0);
  const ventas = ags.reduce((s, a) => s + (a.reservas || 0), 0);
  const best = ags.reduce((b, a) => ((a.dinero || 0) > (b?.dinero || 0) ? a : b), null);
  const fastest = ags
    .filter(a => a.avg_response > 0)
    .reduce((f, a) => (!f || a.avg_response < f.avg_response ? a : f), null);
  const maxDinero = Math.max(1, ...ags.map(a => a.dinero || 0));
  return { dinero, ventas, best, fastest, maxDinero };
});
// Dinero en RD$ (sin decimales) y % de la barra relativa al que más vendió.
const money = n => 'RD$ ' + Number(Math.round(n || 0)).toLocaleString('es-DO');
const moneyBarPct = a =>
  Math.round(((a.dinero || 0) / teamTotals.value.maxDinero) * 100);
// Color del tiempo de respuesta: verde rápido · dorado medio · rojo lento (a corregir).
const rtClass = s => {
  if (!s) return 'm';
  if (s <= 3600) return 'f'; // ≤ 60 min
  if (s <= 10800) return 'm'; // ≤ 180 min
  return 's';
};

const fetchPlatforms = async () => {
  platLoading.value = true;
  try {
    const { data } = await AdvancedMetricsAPI.platforms(baseParams());
    platData.value = data;
  } catch (e) {
    platData.value = null;
  } finally {
    platLoading.value = false;
  }
};

const platforms = computed(() => platData.value?.platforms || []);
const platHighlights = computed(() => platData.value?.highlights || {});
const ncm = computed(() => platData.value?.new_clients_matrix || null);
const ncmCols = [
  { code: 'wa', label: 'WHATSAPP' },
  { code: 'ig', label: 'INSTAGRAM' },
  { code: 'tk', label: 'TIKTOK' },
  { code: 'fb', label: 'FACEBOOK' },
];
const ncmRowTotal = (branchCode) => {
  if (!ncm.value) return 0;
  const row = ncm.value.matrix?.[branchCode] || {};
  return ncmCols.reduce((s, c) => s + (row[c.code] || 0), 0);
};
const ncmColTotal = (chCode) => {
  if (!ncm.value) return 0;
  return (ncm.value.branches || []).reduce((s, b) => s + ((ncm.value.matrix?.[b.code] || {})[chCode] || 0), 0);
};

const PF = {
  WhatsApp: { cls: 'wa', icon: '✆' },
  Instagram: { cls: 'ig', icon: '◎' },
  Facebook: { cls: 'fb', icon: 'f' },
  Messenger: { cls: 'ms', icon: '⌬' },
  TikTok: { cls: 'tk', icon: '♪' },
  Web: { cls: 'web', icon: '◍' },
  Otro: { cls: 'web', icon: '•' },
};
const pfMeta = name => PF[name] || PF.Otro;
const qualityClass = q =>
  ({ Alta: 'g', Media: '', Baja: 'r' })[q] ?? '';

const fetchRealtime = async () => {
  try {
    const params = {};
    if (branchFilter.value) params.branch_id = branchFilter.value;
    const { data } = await AdvancedMetricsAPI.realtime(params);
    rtData.value = data;
  } catch (e) {
    /* silencioso en polling */
  }
};
const rtKpis = computed(() => rtData.value?.kpis || {});
const rtFeed = computed(() => rtData.value?.feed || []);
const rtAlerts = computed(() => rtData.value?.alerts || []);

const stopRtPolling = () => {
  if (rtTimer) {
    clearInterval(rtTimer);
    rtTimer = null;
  }
};
const startRtPolling = () => {
  fetchRealtime();
  stopRtPolling();
  rtTimer = setInterval(fetchRealtime, 20000);
};

// ── Pestaña 5 · Clientes en Vivo (polling 20s, estado actual) ──
const fetchLive = async () => {
  try {
    const params = {};
    if (branchFilter.value) params.branch_id = branchFilter.value;
    const { data } = await AdvancedMetricsAPI.liveClients(params);
    liveData.value = data;
  } catch (e) {
    /* silencioso en polling */
  }
};
const stopLivePolling = () => {
  if (liveTimer) {
    clearInterval(liveTimer);
    liveTimer = null;
  }
};
const startLivePolling = () => {
  fetchLive();
  stopLivePolling();
  liveTimer = setInterval(fetchLive, 20000);
};
const liveBranches = computed(() => liveData.value?.branches || []);
const liveTotals = computed(() => liveData.value?.totals || {});
const liveWinner = computed(() => {
  const arr = liveBranches.value;
  if (!arr.length) return null;
  return [...arr].sort((a, b) => (b[liveSeg.value] || 0) - (a[liveSeg.value] || 0))[0];
});
const liveWinnerVal = computed(() =>
  liveWinner.value ? liveWinner.value[liveSeg.value] || 0 : 0
);
const liveMoreDay = computed(() =>
  (liveTotals.value.wrote_today || 0) >= (liveTotals.value.wrote_yesterday || 0)
    ? 'hoy'
    : 'ayer'
);

const timeAgo = s => {
  if (s == null) return '';
  if (s < 5) return 'ahora';
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  return `${Math.floor(s / 3600)}h`;
};

const selectTab = id => {
  tab.value = id;
  if (id === 'team' && !teamData.value) fetchTeam();
  if (id === 'plat' && !platData.value) fetchPlatforms();
  if (id === 'rt') startRtPolling();
  else stopRtPolling();
  if (id === 'live') startLivePolling();
  else stopLivePolling();
  if (id === 'team') startTeamPolling();
  else stopTeamPolling();
};

// Refetch coherente: la pestaña activa se recarga ya; las demás se invalidan
// para que recarguen al visitarlas con el nuevo período/sucursal.
const applyFilters = () => {
  fetchExecutive();
  if (tab.value === 'team') {
    if (teamMode.value === 'login') fetchTeamLogin();
    else fetchTeam();
  } else {
    teamData.value = null;
    loginData.value = null;
  }
  if (tab.value === 'plat') fetchPlatforms();
  else platData.value = null;
  if (tab.value === 'rt') fetchRealtime();
  if (tab.value === 'live') fetchLive();
};

const setPeriod = id => {
  period.value = id;
  if (id !== 'range') applyFilters();
};

const applyRange = () => {
  if (period.value !== 'range') period.value = 'range';
  applyFilters();
};

onUnmounted(() => {
  stopRtPolling();
  stopLivePolling();
  stopTeamPolling();
});

const gradeLabel = g =>
  ({ excelente: 'Excelente', bueno: 'Bueno', regular: 'Regular', riesgo: 'Riesgo' })[g] || g;
const closePct = a => {
  const base = a.worked ?? a.attended ?? 0;
  return base > 0 ? Math.round((a.closed / base) * 100) : 0;
};

// helpers de formato
const fmt = n => (n == null ? '—' : Number(n).toLocaleString('es-DO'));
const secs = s => {
  if (s == null) return '—';
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return m > 0 ? `${m}m ${r}s` : `${r}s`;
};
const initials = name =>
  (name || '')
    .split(' ')
    .slice(0, 2)
    .map(p => p[0])
    .join('')
    .toUpperCase() || '–';
const circColor = p => (p >= 45 ? 'var(--g)' : p >= 35 ? 'var(--gold)' : 'var(--r)');
// Presencia real del agente (Victor 2026-06-09): 🟢 conectado · 🟡 ocupado · ⚪ desconectado.
const presenceLabel = s =>
  s === 'online' ? 'Conectado' : s === 'busy' ? 'Ocupado' : 'Desconectado';
const rankClass = i => (i === 1 ? 'r1' : '');

onMounted(fetchExecutive);
</script>

<template>
  <div class="am">
    <!-- HEADER · Propuesta 1 línea única condensada (Victor 2026-06-07) -->
    <div class="topbar">
      <div class="topbar-row">
        <div>
          <div class="bc">Informes › Centro de mando</div>
          <h1 class="am-title">Métricas <span class="acc">Avanzadas</span></h1>
        </div>
        <div class="am-actions">
          <span class="live">En vivo</span>
          <span class="am-dot">·</span>
          <span class="am-range">{{ rangeLabel }}</span>
          <button class="am-export">Exportar ↧</button>
        </div>
      </div>
      <div class="am-rule"></div>
    </div>

    <!-- BARRA DE CONTROL · período + filtros en una sola línea -->
    <div class="ctrlbar">
      <div class="period">
        <div
          v-for="o in periodOptions"
          :key="o.id"
          class="pseg"
          :class="{ on: period === o.id }"
          @click="setPeriod(o.id)"
        >
          <span class="ic">{{ o.icon }}</span> {{ o.label }}
          <template v-if="o.id === 'range'"> ▾</template>
        </div>
      </div>

      <div class="ctrl-sep"></div>

      <div class="filters">
        <!-- Sucursal plegada en un solo menú -->
        <div class="fdrop">
          <div class="fchip" :class="{ on: branchFilter }" @click="toggleBranchMenu">
            Sucursal: <b>{{ branchLabel }}</b> <span class="car">▾</span>
          </div>
          <div v-if="branchMenuOpen" class="fmenu">
            <div class="fitem" :class="{ on: !branchFilter }" @click="pickBranch(null)">Todas</div>
            <div
              v-for="b in branches"
              :key="b.id"
              class="fitem"
              :class="{ on: branchFilter === b.id }"
              @click="pickBranch(b.id)"
            >
              <b>{{ b.code }}</b> <span class="fitem-name">{{ b.name }}</span>
            </div>
          </div>
        </div>
        <div class="fchip">Plataforma: <b>Todas</b> <span class="car">▾</span></div>
        <div class="fchip">Agente: <b>Todos</b> <span class="car">▾</span></div>
        <div class="fchip">Estado: <b>Todos</b> <span class="car">▾</span></div>
      </div>
    </div>

    <!-- Panel de rango (aparece solo al elegir "Rango de fecha") -->
    <div v-if="period === 'range'" class="range-panel">
      <div>
        <div class="rlbl">Desde</div>
        <input type="date" class="date-box" v-model="fromStr" :max="toStr" />
      </div>
      <span class="range-arrow">→</span>
      <div>
        <div class="rlbl">Hasta</div>
        <input type="date" class="date-box" v-model="toStr" :min="fromStr" />
      </div>
      <button class="range-go" @click="applyRange">Aplicar rango</button>
      <div class="range-hint">Los KPIs y la tabla de abajo se recalculan para este rango exacto.</div>
    </div>

    <!-- TABS · subrayado dorado -->
    <div class="tabs">
      <div
        v-for="t in tabs"
        :key="t.id"
        class="tab"
        :class="{ active: tab === t.id }"
        @click="selectTab(t.id)"
      >
        <span class="ti">{{ t.icon }}</span> {{ t.label }}
      </div>
    </div>

    <!-- ══════ P1 · EJECUTIVO (datos reales) ══════ -->
    <div v-show="tab === 'ejec'" class="view">
      <div v-if="loading" class="loading">Cargando métricas…</div>
      <template v-else>
        <!-- KPIs -->
        <div class="kgrid">
          <div class="kpi card">
            <div class="ktop"><div class="kico">💬</div></div>
            <div class="knum">{{ fmt(kpis.messages_today?.value) }}</div>
            <div class="kname">Mensajes de hoy</div>
          </div>
          <div class="kpi card">
            <div class="ktop"><div class="kico">📈</div></div>
            <div class="knum">{{ fmt(kpis.messages_week?.value) }}</div>
            <div class="kname">Mensajes de la semana</div>
          </div>
          <div class="kpi card">
            <div class="ktop"><div class="kico">🙋</div></div>
            <div class="knum">{{ fmt(kpis.clients_attended?.value) }}</div>
            <div class="kname">Clientes atendidos</div>
          </div>
          <div class="kpi card">
            <div class="ktop"><div class="kico">⏱</div></div>
            <div class="knum">{{ secs(kpis.avg_response_time?.seconds) }}</div>
            <div class="kname">Tiempo prom. de respuesta</div>
          </div>
          <div class="kpi card">
            <div class="ktop"><div class="kico">🏆</div><span class="bpill">{{ kpis.top_branch?.code }}</span></div>
            <div class="knum">{{ kpis.top_branch?.pct }}<small>%</small></div>
            <div class="kname">Sucursal más activa</div>
          </div>
          <div class="kpi card">
            <div class="ktop"><div class="kico">📲</div><span class="kdelta kup">{{ kpis.top_platform?.name }}</span></div>
            <div class="knum">{{ kpis.top_platform?.pct }}<small>%</small></div>
            <div class="kname">Plataforma con más mensajes</div>
          </div>
          <div class="kpi card">
            <div class="ktop"><div class="kico">⭐</div><span class="kdelta kup">{{ kpis.top_agent?.score }} pts</span></div>
            <div class="knum sm">{{ kpis.top_agent?.name }}</div>
            <div class="kname">Agente con mejor rendimiento</div>
          </div>
          <div class="kpi card">
            <div class="ktop"><div class="kico">🔔</div></div>
            <div class="knum" :class="{ red: (kpis.pending?.value || 0) > 0 }">{{ fmt(kpis.pending?.value) }}</div>
            <div class="kname">Pendientes sin responder</div>
          </div>
        </div>

        <!-- MAPA POR SUCURSAL -->
        <div class="sec-head">
          <div class="sec-title"><span class="dot"></span> Mapa de rendimiento por sucursal</div>
          <div class="hint">Ranking automático por volumen de mensajes</div>
        </div>
        <div v-if="branches.length" class="bgrid">
          <div v-for="b in branches" :key="b.id" class="bcard card">
            <div class="bhead">
              <div class="bname">
                <span class="rankbadge" :class="rankClass(b.rank)">{{ b.rank }}</span> {{ b.name }}
              </div>
              <span class="bpill">{{ b.code }}</span>
            </div>
            <div class="bmetrics">
              <div class="bm"><div class="bv">{{ fmt(b.messages) }}</div><div class="bl">Mensajes</div></div>
              <div class="bm"><div class="bv">{{ fmt(b.clients) }}</div><div class="bl">Clientes</div></div>
              <div class="bm"><div class="bv">{{ secs(b.avg_response) }}</div><div class="bl">T. resp.</div></div>
            </div>
            <div class="circwrap">
              <div class="circ" :style="{ '--p': b.conversion_pct, '--c': circColor(b.conversion_pct) }">
                <span class="cv">{{ b.conversion_pct }}%</span>
              </div>
              <div class="conv-side">
                <div class="barline">
                  <span class="bk">Cerrados</span>
                  <span class="track"><i class="g" :style="{ width: Math.min(100, b.closed * 2) + '%' }"></i></span>
                  <span class="bn">{{ b.closed }}</span>
                </div>
                <div class="barline">
                  <span class="bk">Pendientes</span>
                  <span class="track"><i :style="{ width: Math.min(100, b.pending * 6) + '%' }"></i></span>
                  <span class="bn">{{ b.pending }}</span>
                </div>
                <div class="barline">
                  <span class="bk">Agentes ✦</span>
                  <span class="track"><i class="b" :style="{ width: Math.min(100, b.agents * 12) + '%' }"></i></span>
                  <span class="bn">{{ b.agents }}</span>
                </div>
                <div class="topch">Top canal: <span class="bpill green">{{ b.top_channel }}</span></div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="empty card">Sin datos de sucursales en este rango.</div>

        <!-- RANKING DE AGENTES -->
        <div class="sec-head">
          <div class="sec-title"><span class="dot"></span> Ranking de agentes · Top 5</div>
          <div class="hint">Puntaje global ponderado (volumen · velocidad · cierre)</div>
        </div>
        <div class="card lb">
          <div class="lbhead">
            <span class="lbl ctr">#</span><span></span><span class="lbl">Agente</span>
            <span class="lbl">Clientes / Veloc.</span><span class="lbl">Cierre / Activas</span>
            <span class="lbl rgt">Puntaje</span>
          </div>
          <div v-for="(a, i) in agents" :key="a.id" class="lbrow">
            <div class="lbrank" :class="['t' + (i + 1)]">{{ i + 1 }}</div>
            <div class="av">{{ initials(a.name) }}</div>
            <div class="lbinfo"><div class="ln">{{ a.name }}</div><div class="ls">{{ a.branch || '—' }} · {{ a.role }}</div></div>
            <div class="lbstat"><div class="ls-v">{{ a.attended }} <span class="dim">· {{ secs(a.avg_response) }}</span></div><div class="ls-l">atendidos</div></div>
            <div class="lbstat"><div class="ls-v">{{ a.close_pct }}% <span class="dim">· {{ a.active }}</span></div><div class="ls-l">cierre</div></div>
            <div class="lbscore">{{ a.score }}</div>
          </div>
          <div v-if="!agents.length" class="empty">Sin actividad de agentes en este rango.</div>
        </div>
      </template>
    </div>

    <!-- ══════ P3 · EFICIENCIA DEL EQUIPO (datos reales) ══════ -->
    <div v-show="tab === 'team'" class="view">
      <div v-if="teamLoading" class="loading">Cargando equipo…</div>
      <template v-else>
        <!-- Totales del día (Victor 2026-06-07): dinero, ventas, mejor vendedor, respuesta más rápida -->
        <div v-if="teamMode === 'performance' && teamAgents.length" class="kgrid" style="margin-bottom: 6px">
          <div class="kpi card"><div class="ktop"><div class="kico">💰</div></div><div class="knum gold">{{ money(teamTotals.dinero) }}</div><div class="kname">Dinero del día</div></div>
          <div class="kpi card"><div class="ktop"><div class="kico">🏆</div></div><div class="knum g">{{ fmt(teamTotals.ventas) }}</div><div class="kname">Ventas del día (reservas)</div></div>
          <div class="kpi card"><div class="ktop"><div class="kico">👑</div></div><div class="knum sm gold">{{ teamTotals.best?.name || '—' }}</div><div class="kname">Mejor vendedor · {{ money(teamTotals.best?.dinero) }}</div></div>
          <div class="kpi card"><div class="ktop"><div class="kico">⚡</div></div><div class="knum sm">{{ teamTotals.fastest?.name || '—' }}</div><div class="kname">Respuesta más rápida · {{ secs(teamTotals.fastest?.avg_response) }}</div></div>
        </div>
        <div class="card legend">
          <div class="seg" style="margin-right: auto">
            <div class="segbtn" :class="{ on: teamMode === 'performance' }" @click="setTeamMode('performance')">💼 Rendimiento</div>
            <div class="segbtn" :class="{ on: teamMode === 'login' }" @click="setTeamMode('login')">🕐 Inicio de sesión</div>
          </div>
          <template v-if="teamMode === 'performance'">
            <span class="rangetag">📍 Hoy · ordenado por ventas</span>
            <span class="grade g-exc">● Responde rápido</span>
            <span class="grade g-reg">● Medio</span>
            <span class="grade g-rie">● Tarda (a corregir)</span>
          </template>
          <span v-else class="rangetag">⏱ Entrada esperada · {{ loginData?.expected_entry_label || '8:30 a.m.' }}</span>
        </div>
        <!-- Acordeón por sucursal (Victor 2026-06-09): todos los agentes, clic despliega -->
        <div v-if="teamMode === 'performance' && teamBranches.length">
          <div v-for="b in teamBranches" :key="b.id" class="card bacc" :class="{ open: isBranchOpen(b.id) }">
            <div class="lg-bhead bclick" @click="toggleBranch(b.id)">
              <div class="lg-bname"><span class="chev">▶</span> Sucursal {{ b.name }}</div>
              <div class="lg-bsum">
                <span class="lg-cnt">{{ b.total }} agentes</span>
                <span class="lg-g">🏆 {{ b.ventas }} ventas</span>
                <span class="lg-d">💰 {{ money(b.dinero) }}</span>
                <span class="lg-pr">⏱ prom. {{ secs(b.avg_response) }}</span>
              </div>
            </div>
            <div v-show="isBranchOpen(b.id)" class="bbody">
              <table class="tbl">
                <thead>
                  <tr>
                    <th class="l">Agente</th>
                    <th class="hi">🗣 Atend.</th>
                    <th class="hi">🏆 Ganadas</th>
                    <th class="hi">💰 Dinero generado</th>
                    <th>❌ Perd.</th>
                    <th>💬 Mi chat</th>
                    <th>🔥 Urg.</th>
                    <th class="rgt">⏱ T. respuesta</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(a, i) in b.agents" :key="a.id" class="agent-row" :class="{ topseller: i === 0 && a.reservas > 0 }">
                    <td class="l mcell" @click="openAgent(a)">
                      <div class="agc">
                        <div class="av sm" :class="'pres-' + (a.online || 'offline')">
                          {{ initials(a.name) }}<span class="presdot" :title="presenceLabel(a.online)"></span>
                        </div>
                        <div><div class="an">{{ a.name }}</div><div class="ar">{{ a.branch || '—' }} · {{ a.role }}</div></div>
                      </div>
                    </td>
                    <td class="mcell" @click="openMetric(a, 'atendidos', '🗣 Atendidos')"><span class="big gold">{{ fmt(a.attended) }}</span></td>
                    <td class="mcell" @click="openMetric(a, 'ganadas', '🏆 Ventas ganadas')"><span class="big g">{{ a.reservas ?? 0 }}</span></td>
                    <td class="moneycell mcell" @click="openMetric(a, 'dinero', '💰 Dinero generado')">
                      <span class="mv">{{ money(a.dinero) }}</span>
                      <span class="mbar"><i :style="{ width: moneyBarPct(a) + '%' }"></i></span>
                    </td>
                    <td class="mcell" @click="openMetric(a, 'perdidas', '❌ Venta perdida')"><span class="big" :class="{ red: a.lost > 0, dim: !a.lost }">{{ a.lost ?? 0 }}</span></td>
                    <td class="mcell" @click="openMetric(a, 'mi_chat', '💬 Mi chat')">{{ fmt(a.mi_chat) }}</td>
                    <td class="mcell" @click="openMetric(a, 'urgentes', '🔥 En urgentes')"><span class="big" :class="{ red: a.urgent > 0, dim: !a.urgent }">{{ a.urgent ?? 0 }}</span></td>
                    <td class="rgt"><span class="rtcell" :class="'rt-' + rtClass(a.avg_response)"><span class="rtdot"></span>{{ secs(a.avg_response) }}</span></td>
                  </tr>
                  <tr v-if="!b.agents.length" class="agent-row"><td colspan="8" class="bempty">Sin agentes en esta sucursal.</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div v-else-if="teamMode === 'performance'" class="empty card">Sin sucursales configuradas.</div>

        <!-- destacados automáticos -->
        <template v-if="teamMode === 'performance'">
          <div class="sec-head"><div class="sec-title"><span class="dot"></span> Destacados automáticos</div></div>
          <div class="kgrid">
            <div class="kpi card"><div class="ktop"><div class="kico">🏅</div></div><div class="knum sm">{{ highlights.top_performance || '—' }}</div><div class="kname">Top rendimiento</div></div>
            <div class="kpi card"><div class="ktop"><div class="kico">⚡</div><span v-if="highlights.fastest" class="kdelta kup">{{ secs(highlights.fastest.seconds) }}</span></div><div class="knum sm">{{ highlights.fastest?.name || '—' }}</div><div class="kname">Más rápido</div></div>
            <div class="kpi card"><div class="ktop"><div class="kico">🏆</div><span v-if="highlights.most_efficient_branch" class="bpill">{{ highlights.most_efficient_branch.code }}</span></div><div class="knum sm">{{ highlights.most_efficient_branch?.name || '—' }}</div><div class="kname">Sucursal más eficiente</div></div>
            <div class="kpi card"><div class="ktop"><div class="kico">💎</div><span v-if="highlights.best_conversion" class="kdelta kup">{{ highlights.best_conversion.pct }}%</span></div><div class="knum sm">{{ highlights.best_conversion?.code || '—' }}</div><div class="kname">Mayor conversión</div></div>
          </div>
        </template>

        <!-- ░░ Modo INICIO DE SESIÓN · por sucursal (Victor 2026-06-09) ░░ -->
        <template v-else>
          <div v-if="loginLoading" class="loading">Cargando inicios de sesión…</div>
          <template v-else>
            <div class="kgrid" style="margin-bottom: 6px">
              <div class="kpi card"><div class="ktop"><div class="kico">👥</div></div><div class="knum">{{ loginSummary.total ?? 0 }}</div><div class="kname">Agentes</div></div>
              <div class="kpi card"><div class="ktop"><div class="kico">✅</div></div><div class="knum g">{{ loginSummary.ontime ?? 0 }}</div><div class="kname">A tiempo</div></div>
              <div class="kpi card"><div class="ktop"><div class="kico">⏰</div></div><div class="knum" :class="{ red: loginSummary.late }">{{ loginSummary.late ?? 0 }}</div><div class="kname">Tarde</div></div>
              <div class="kpi card"><div class="ktop"><div class="kico">🕐</div></div><div class="knum sm gold">{{ loginSummary.avg_entry_label || '—' }}</div><div class="kname">Entrada promedio · {{ loginSummary.missing ?? 0 }} sin iniciar</div></div>
            </div>
            <div v-if="loginBranches.length">
              <div v-for="b in loginBranches" :key="b.id" class="card tblcard" style="margin-bottom: 10px">
                <div class="lg-bhead">
                  <div class="lg-bname"><span class="dot"></span> Sucursal {{ b.name }}</div>
                  <div class="lg-bsum">
                    <span class="lg-cnt">{{ b.total }} agentes</span>
                    <span class="lg-g">{{ b.ontime }} a tiempo</span>
                    <span class="lg-r" :class="{ dimr: !b.late }">{{ b.late }} tarde</span>
                    <span class="lg-pr">prom. {{ b.avg_entry_label }}</span>
                  </div>
                </div>
                <table class="tbl">
                  <thead><tr><th class="l">Agente</th><th>Entrada</th><th class="rgt">Estado</th></tr></thead>
                  <tbody>
                    <tr v-for="a in b.agents" :key="a.id" class="agent-row">
                      <td class="l">
                        <div class="agc">
                          <div class="av sm">{{ initials(a.name) }}</div>
                          <div><div class="an">{{ a.name }}</div><div class="ar">{{ a.role }}</div></div>
                        </div>
                      </td>
                      <td>
                        <span class="big" :class="{ red: a.status === 'late', gold: a.status === 'ontime', dim: a.status === 'missing' }">{{ a.entry_label }}</span>
                        <span v-if="a.multi && a.days" class="lg-days">· {{ a.days }}d</span>
                      </td>
                      <td class="rgt"><span class="lg-pill" :class="'lg-' + a.status">{{ statusLabel(a) }}</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div v-else class="empty card">Sin registros de inicio de sesión en el período.</div>
          </template>
        </template>
      </template>
    </div>

    <!-- ══════ P2 · POR PLATAFORMA (datos reales) ══════ -->
    <div v-show="tab === 'plat'" class="view">
      <div v-if="platLoading" class="loading">Cargando plataformas…</div>
      <template v-else>
        <div class="sec-head" style="margin-top: 4px">
          <div class="sec-title"><span class="dot"></span> Analíticas por plataforma digital</div>
          <div class="hint">Mensajes · clientes · conversión · respuesta · crecimiento</div>
        </div>
        <div v-if="platforms.length" class="pfgrid">
          <div v-for="p in platforms" :key="p.name" class="pf card">
            <div class="pfh">
              <div class="pfico" :class="pfMeta(p.name).cls">{{ pfMeta(p.name).icon }}</div>
              <div><div class="pfn">{{ p.name }}</div><div class="pft">Canal digital</div></div>
              <span class="growth" :class="p.growth >= 0 ? 'kup' : 'kdn'">{{ p.growth >= 0 ? '▲' : '▼' }} {{ Math.abs(p.growth) }}%</span>
            </div>
            <div class="pfstats">
              <div class="pfs"><div class="v">{{ fmt(p.messages) }}</div><div class="l">Mensajes</div></div>
              <div class="pfs"><div class="v">{{ fmt(p.clients) }}</div><div class="l">Clientes</div></div>
              <div class="pfs"><div class="v g">{{ fmt(p.closed) }}</div><div class="l">Cerrados</div></div>
              <div class="pfs"><div class="v">{{ secs(p.avg_response) }}</div><div class="l">T. resp.</div></div>
            </div>
            <div class="barline">
              <span class="bk">Conversión</span>
              <span class="track"><i class="g" :style="{ width: Math.min(100, p.conversion_pct) + '%' }"></i></span>
              <span class="bn">{{ p.conversion_pct }}%</span>
            </div>
            <div class="barline">
              <span class="bk">Calidad</span>
              <span class="track"><i :class="qualityClass(p.quality)" :style="{ width: (p.quality === 'Alta' ? 88 : p.quality === 'Media' ? 60 : 38) + '%' }"></i></span>
              <span class="bn">{{ p.quality }}</span>
            </div>
          </div>
        </div>
        <div v-else class="empty card">Sin mensajes por plataforma en este rango.</div>

        <div v-if="platforms.length" class="card sumrow">
          <div class="sumitem"><div class="suml">Más mensajes</div><div class="sumv">{{ platHighlights.most_messages?.name || '—' }} <span class="g">{{ fmt(platHighlights.most_messages?.value) }}</span></div></div>
          <div class="sumitem"><div class="suml">Más cerró</div><div class="sumv">{{ platHighlights.most_closed?.name || '—' }} <span class="g">{{ fmt(platHighlights.most_closed?.value) }}</span></div></div>
          <div class="sumitem"><div class="suml">Mayor crecimiento</div><div class="sumv">{{ platHighlights.most_growth?.name || '—' }} <span class="gold">▲{{ platHighlights.most_growth?.value }}%</span></div></div>
          <div class="sumitem"><div class="suml">Peor respuesta</div><div class="sumv">{{ platHighlights.worst_response?.name || '—' }} <span class="red">{{ secs(platHighlights.worst_response?.seconds) }}</span></div></div>
        </div>

        <!-- Matriz Clientes Nuevos × Sucursal × Canal (P2 · Victor 2026-05-31) -->
        <div v-if="ncm && ncm.branches && ncm.branches.length" class="ncm-card card">
          <div class="ncm-head">
            <div>
              <div class="ncm-title">CLIENTES NUEVOS · POR SUCURSAL Y CANAL</div>
              <div class="ncm-sub">Clientes que escribieron por primera vez en el rango seleccionado</div>
            </div>
            <div class="ncm-total">
              <span class="ncm-total-label">Total:</span>
              <span class="ncm-total-value">{{ fmt(ncm.total) }}</span>
              <span class="ncm-delta" :class="ncm.growth >= 0 ? 'ncm-up' : 'ncm-down'">
                {{ ncm.growth >= 0 ? '▲' : '▼' }} {{ Math.abs(ncm.growth) }}%
              </span>
            </div>
          </div>
          <table class="ncm-table">
            <thead>
              <tr>
                <th class="left">SUCURSAL</th>
                <th v-for="c in ncmCols" :key="c.code" class="right">{{ c.label }}</th>
                <th class="right gold-th">TOTAL</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="b in ncm.branches" :key="b.id" class="ncm-row">
                <td class="left">
                  {{ b.name }} <span class="ncm-code">{{ b.code }}</span>
                </td>
                <td v-for="c in ncmCols" :key="c.code" class="right num"
                    :class="((ncm.matrix[b.code]||{})[c.code]||0) === 0 ? 'dim' : ''">
                  {{ (ncm.matrix[b.code]||{})[c.code] || 0 }}
                </td>
                <td class="right num ncm-row-total">{{ ncmRowTotal(b.code) }}</td>
              </tr>
              <tr class="ncm-total-row">
                <td class="left ncm-total-label-cell">TOTAL</td>
                <td v-for="c in ncmCols" :key="c.code" class="right num">{{ ncmColTotal(c.code) }}</td>
                <td class="right num ncm-grand-total">{{ ncm.total }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>

    <!-- ══════ P4 · TIEMPO REAL (datos en vivo · polling) ══════ -->
    <div v-show="tab === 'rt'" class="view">
      <div class="kgrid" style="margin-bottom: 6px">
        <div class="kpi card"><div class="ktop"><div class="kico">🟢</div><span class="live">vivo</span></div><div class="knum">{{ fmt(rtKpis.open) }}</div><div class="kname">Conversaciones abiertas</div></div>
        <div class="kpi card"><div class="ktop"><div class="kico">👤</div><span class="live">vivo</span></div><div class="knum">{{ fmt(rtKpis.active_agents) }}</div><div class="kname">Agentes activos ahora</div></div>
        <div class="kpi card"><div class="ktop"><div class="kico">⏳</div></div><div class="knum" :class="{ red: (rtKpis.waiting || 0) > 0 }">{{ fmt(rtKpis.waiting) }}</div><div class="kname">Clientes esperando</div></div>
        <div class="kpi card"><div class="ktop"><div class="kico">⌛</div></div><div class="knum" :class="{ red: (rtKpis.max_wait_seconds || 0) > 600 }">{{ secs(rtKpis.max_wait_seconds) }}</div><div class="kname">Mayor espera actual</div></div>
      </div>

      <div class="rtgrid">
        <div>
          <div class="sec-head"><div class="sec-title"><span class="dot"></span> Actividad en tiempo real</div><div class="hint">Auto-actualiza cada 20s</div></div>
          <div class="card feed">
            <div v-for="(f, i) in rtFeed" :key="i" class="feedrow">
              <span class="fdot" :class="f.direction === 'in' ? '' : 'b'"></span>
              <span class="ftxt"><b>{{ f.platform }}<template v-if="f.branch"> · {{ f.branch }}</template></b> — {{ f.direction === 'in' ? 'mensaje de' : 'respuesta a' }} {{ f.contact || 'cliente' }}</span>
              <span class="ftime">{{ timeAgo(f.seconds_ago) }}</span>
            </div>
            <div v-if="!rtFeed.length" class="empty">Sin actividad reciente.</div>
          </div>
        </div>
        <div>
          <div class="sec-head"><div class="sec-title"><span class="dot"></span> Alertas inteligentes</div></div>
          <div v-for="(a, i) in rtAlerts" :key="i" class="alert" :class="a.type">
            <span class="aico">{{ a.icon }}</span><span class="atxt">{{ a.text }}</span>
          </div>
          <div v-if="!rtAlerts.length" class="empty card">Todo en orden · sin alertas.</div>
        </div>
      </div>
    </div>

    <!-- ══════ P5 · CLIENTES EN VIVO POR SUCURSAL (en vivo · polling) ══════ -->
    <div v-show="tab === 'live'" class="view">
      <!-- KPIs en vivo (totales, suma todas las plataformas) -->
      <div class="kgrid" style="margin-bottom: 6px">
        <div class="kpi card"><div class="ktop"><div class="kico">🟢</div><span class="live">vivo</span></div><div class="knum">{{ fmt(liveTotals.todos) }}</div><div class="kname">Clientes en vivo · Todos</div></div>
        <div class="kpi card"><div class="ktop"><div class="kico">🚨</div></div><div class="knum" :class="{ red: (liveTotals.urgentes || 0) > 0 }">{{ fmt(liveTotals.urgentes) }}</div><div class="kname">En urgentes ahora</div></div>
        <div class="kpi card"><div class="ktop"><div class="kico">🙋</div></div><div class="knum">{{ fmt(liveTotals.michat) }}</div><div class="kname">En mi chat</div></div>
        <div class="kpi card"><div class="ktop"><div class="kico">✍️</div></div><div class="knum">{{ fmt(liveTotals.wrote_today) }}</div><div class="kname">Escribieron hoy <span class="dim">· ayer {{ fmt(liveTotals.wrote_yesterday) }}</span></div></div>
      </div>

      <!-- Controles: segmento + día -->
      <div class="live-ctrl">
        <div class="seg">
          <div class="segbtn" :class="{ on: liveSeg === 'todos' }" @click="liveSeg = 'todos'">Todos</div>
          <div class="segbtn" :class="{ on: liveSeg === 'urgentes' }" @click="liveSeg = 'urgentes'">Urgentes</div>
          <div class="segbtn" :class="{ on: liveSeg === 'michat' }" @click="liveSeg = 'michat'">Mi Chat</div>
        </div>
        <div class="seg day">
          <div class="segbtn" :class="{ on: liveDay === 'ayer' }" @click="liveDay = 'ayer'">Ayer</div>
          <div class="segbtn" :class="{ on: liveDay === 'hoy' }" @click="liveDay = 'hoy'">Hoy</div>
        </div>
      </div>

      <!-- Veredicto: ¿dónde hubo más? -->
      <div v-if="liveBranches.length" class="verdict">
        <span class="vem">🏆</span>
        <div class="vtx">
          Hoy escribieron <b>{{ fmt(liveTotals.wrote_today) }}</b> clientes · ayer {{ fmt(liveTotals.wrote_yesterday) }} ·
          hubo más <b>{{ liveMoreDay === 'hoy' ? 'HOY' : 'AYER' }}</b>.
          <span class="vsub">Más carga en vivo ({{ segLabel[liveSeg] }}): {{ liveWinner?.name }} con {{ liveWinnerVal }} clientes.</span>
        </div>
      </div>

      <!-- Tarjetas por sucursal -->
      <div v-if="liveBranches.length" class="bgrid live-grid">
        <div v-for="b in liveBranches" :key="b.id" class="bcard card" :class="{ win: liveWinner && b.id === liveWinner.id }">
          <div class="bhead">
            <span class="bpill">{{ b.code }}</span>
            <span class="bname-lg">{{ b.name }}</span>
            <span v-if="liveWinner && b.id === liveWinner.id" class="crown">👑</span>
          </div>
          <div class="live-big" :class="{ gold: liveWinner && b.id === liveWinner.id }">{{ fmt(b[liveSeg]) }}</div>
          <div class="live-cap">{{ segLabel[liveSeg] }} · en vivo</div>
          <div class="live-cmp">
            <template v-if="liveDay === 'hoy'">
              Escribieron hoy: <b>{{ fmt(b.wrote_today) }}</b>
              <span :class="b.wrote_today - b.wrote_yesterday >= 0 ? 'up' : 'dn'">
                {{ b.wrote_today - b.wrote_yesterday >= 0 ? '▲ +' : '▼ ' }}{{ Math.abs(b.wrote_today - b.wrote_yesterday) }} vs ayer
              </span>
            </template>
            <template v-else>Escribieron ayer: <b>{{ fmt(b.wrote_yesterday) }}</b></template>
          </div>
          <div class="live-mini">
            <div><div class="lm-v u">{{ fmt(b.urgentes) }}</div><div class="lm-l">Urgentes</div></div>
            <div><div class="lm-v m">{{ fmt(b.michat) }}</div><div class="lm-l">Mi Chat</div></div>
            <div><div class="lm-v">{{ fmt(b.todos) }}</div><div class="lm-l">Todos</div></div>
          </div>
        </div>
      </div>
      <div v-else class="empty card">Cargando clientes en vivo…</div>

      <div class="live-note">Datos en vivo · auto-actualiza cada 20s · clientes = contactos únicos · suma WhatsApp + Instagram + Facebook por sucursal · "escribieron" = clientes con mensaje entrante ese día (hora RD).</div>
    </div>

    <!-- Expediente del agente (Victor 2026-06-06) -->
    <AgentDossierModal
      :show="dossier.show"
      :agent-id="dossier.id"
      :agent-name="dossier.name"
      :metric="dossier.metric"
      :metric-label="dossier.metricLabel"
      :branch-id="branchFilter"
      @close="closeDossier"
    />
  </div>
</template>

<style scoped>
.am {
  --gold: #fbbf24; --gold-d: #d97706; --g: #22c55e; --g2: #16a34a; --r: #ef4444;
  --w55: rgba(255, 255, 255, 0.55); --w40: rgba(255, 255, 255, 0.4);
  --card: rgba(10, 10, 10, 0.88); --bd: rgba(255, 255, 255, 0.1);
  font-family: -apple-system, system-ui, sans-serif; color: #fff;
  background: radial-gradient(ellipse 80% 50% at 50% 0%, rgba(251, 191, 36, 0.06), transparent 70%), #050505;
  min-height: 100%; padding: 24px 26px 60px; border-radius: 16px;
}
/* Header monocromo hairline (Propuesta 3, Victor 2026-06-06): breadcrumb fino,
   título grande con acento dorado, línea fina dorada y acciones como texto. */
.topbar { margin-bottom: 16px; }
.topbar-row { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.bc { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 2px; color: var(--w40); }
.am-title { font-size: 24px; font-weight: 800; letter-spacing: -0.3px; margin-top: 5px; } .acc { color: var(--gold); }
.am-rule { height: 1px; background: linear-gradient(90deg, rgba(251, 191, 36, 0.5), transparent); margin: 14px 0 0; }
.am-actions { display: flex; align-items: center; gap: 9px; font-size: 12px; color: var(--w55); }
.am-dot { color: var(--w40); }
.am-range { color: var(--w55); font-weight: 600; }
.am-export { background: none; border: none; cursor: pointer; font-family: inherit; font-weight: 700; font-size: 12px; color: var(--gold); border-bottom: 1px solid rgba(251, 191, 36, 0.5); padding: 2px 0; transition: 0.18s; }
.am-export:hover { color: var(--gold-d); border-color: var(--gold); }
.live { display: inline-flex; align-items: center; gap: 5px; font-size: 9.5px; font-weight: 800; letter-spacing: 1px; text-transform: uppercase; color: var(--g); }
.live::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: var(--g); box-shadow: 0 0 6px var(--g); animation: pulse 1.6s ease-in-out infinite; }
@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
.hbtns { display: flex; gap: 9px; align-items: center; }
.btn { border: none; cursor: pointer; font-family: inherit; font-weight: 800; border-radius: 10px; transition: 0.18s; }
.btn-sec { background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(255, 255, 255, 0.12); color: #fff; padding: 9px 15px; font-size: 12px; }
.btn-sec:hover { background: rgba(251, 191, 36, 0.1); border-color: rgba(251, 191, 36, 0.3); color: var(--gold); transform: translateY(-2px); }
.btn-gold { background: linear-gradient(135deg, var(--gold), var(--gold-d)); color: #000; padding: 9px 16px; font-size: 12px; box-shadow: 0 0 16px rgba(251, 191, 36, 0.3); }
.btn-gold:hover { transform: translateY(-2px); }
/* Barra de control: período + filtros en una línea, separados por hairline */
.ctrlbar { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin-bottom: 16px; }
.ctrl-sep { width: 1px; align-self: stretch; min-height: 26px; background: rgba(255, 255, 255, 0.08); }
.filters { display: flex; gap: 9px; flex-wrap: wrap; }
.fchip { position: relative; display: inline-flex; align-items: center; gap: 6px; padding: 8px 13px; border-radius: 10px; background: var(--card); border: 1px solid var(--bd); font-size: 11.5px; font-weight: 700; color: var(--w55); cursor: pointer; transition: 0.18s; }
.fchip:hover { border-color: rgba(251, 191, 36, 0.3); color: #fff; }
.fchip b { color: #fff; }
.fchip .car { color: var(--w40); font-size: 9px; }
.fchip.on { background: rgba(251, 191, 36, 0.1); border-color: rgba(251, 191, 36, 0.3); color: var(--gold); }
.fchip.on b { color: var(--gold); }
.fchip.on .car { color: var(--gold); }
/* Menú desplegable de Sucursal */
.fdrop { position: relative; }
.fmenu { position: absolute; top: calc(100% + 6px); left: 0; z-index: 30; min-width: 200px; background: #131316; border: 1px solid var(--bd); border-radius: 12px; padding: 5px; box-shadow: 0 20px 50px -20px rgba(0, 0, 0, 0.9); }
.fitem { display: flex; align-items: center; gap: 8px; padding: 9px 11px; border-radius: 8px; font-size: 12px; font-weight: 700; color: var(--w55); cursor: pointer; transition: 0.14s; }
.fitem:hover { background: rgba(255, 255, 255, 0.05); color: #fff; }
.fitem b { color: #fff; }
.fitem-name { color: var(--w40); font-weight: 600; font-size: 11px; }
.fitem.on { color: var(--gold); }
.fitem.on b { color: var(--gold); }
.tabs { display: flex; gap: 4px; margin-bottom: 22px; flex-wrap: wrap; border-bottom: 1px solid rgba(255, 255, 255, 0.07); }
.tab { position: relative; display: inline-flex; align-items: center; gap: 8px; padding: 11px 15px; cursor: pointer; font-size: 12.5px; font-weight: 700; color: var(--w55); transition: 0.18s; user-select: none; }
.tab:hover { color: #fff; }
.tab.active { color: var(--gold); }
.tab.active::after { content: ''; position: absolute; left: 14px; right: 14px; bottom: -1px; height: 2px; background: var(--gold); border-radius: 2px; }
.card { position: relative; background: var(--card); border: 1.5px solid var(--bd); border-radius: 16px; box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.04) inset, 0 14px 36px rgba(0, 0, 0, 0.55); overflow: hidden; }
.card::before { content: ''; position: absolute; inset: 0; z-index: 1; pointer-events: none; border-radius: inherit; background: linear-gradient(120deg, transparent 12%, rgba(255, 255, 255, 0.07) 22%, transparent 24%), radial-gradient(circle at 100% 0%, rgba(255, 255, 255, 0.1), transparent 60%); }
.card > * { position: relative; z-index: 2; }
.sec-head { display: flex; align-items: center; justify-content: space-between; margin: 28px 0 14px; }
.sec-title { font-size: 14px; font-weight: 800; display: flex; align-items: center; gap: 9px; }
.sec-title .dot { width: 7px; height: 7px; border-radius: 2px; background: var(--gold); box-shadow: 0 0 8px rgba(251, 191, 36, 0.6); }
.hint { font-size: 10.5px; color: var(--w40); font-weight: 600; }
.lbl { font-size: 9.5px; font-weight: 800; text-transform: uppercase; letter-spacing: 1px; color: var(--w40); } .lbl.ctr { text-align: center; } .lbl.rgt { text-align: right; }
.bpill { display: inline-flex; align-items: center; padding: 3px 9px; border-radius: 6px; background: rgba(251, 191, 36, 0.1); color: var(--gold); border: 1px solid rgba(251, 191, 36, 0.3); font-size: 10px; font-weight: 800; letter-spacing: 0.5px; }
.bpill.green { background: rgba(34, 197, 94, 0.1); border-color: rgba(34, 197, 94, 0.3); color: var(--g); }
.kgrid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
.kpi { padding: 16px 17px; transition: 0.2s; }
.kpi:hover { transform: translateY(-4px); border-color: rgba(251, 191, 36, 0.3); }
.ktop { display: flex; align-items: center; justify-content: space-between; margin-bottom: 11px; min-height: 30px; }
.kico { width: 30px; height: 30px; border-radius: 9px; display: grid; place-items: center; font-size: 15px; background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.08); }
.kdelta { font-size: 10px; font-weight: 800; padding: 2px 7px; border-radius: 6px; } .kup { color: var(--g); background: rgba(34, 197, 94, 0.12); }
.knum { font-size: 30px; font-weight: 800; letter-spacing: -1px; line-height: 1; } .knum.sm { font-size: 19px; padding-top: 7px; } .knum.red { color: var(--r); } .knum small { font-size: 14px; color: var(--w55); }
.kname { font-size: 11px; font-weight: 700; color: var(--w55); margin-top: 7px; }
.bgrid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 14px; }
.bcard { padding: 18px 19px; transition: 0.2s; } .bcard:hover { transform: translateY(-3px); border-color: rgba(251, 191, 36, 0.25); }
.bhead { display: flex; align-items: center; justify-content: space-between; margin-bottom: 15px; }
.bname { font-size: 15px; font-weight: 800; display: flex; align-items: center; gap: 9px; }
.rankbadge { width: 24px; height: 24px; border-radius: 7px; display: grid; place-items: center; font-size: 11px; font-weight: 800; background: rgba(251, 191, 36, 0.14); color: var(--gold); border: 1px solid rgba(251, 191, 36, 0.3); }
.rankbadge.r1 { background: linear-gradient(135deg, var(--gold), var(--gold-d)); color: #000; border: none; }
.bmetrics { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 15px; }
.bm { background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 10px; padding: 10px 11px; }
.bv { font-size: 18px; font-weight: 800; letter-spacing: -0.5px; } .bl { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: var(--w40); margin-top: 3px; }
.circwrap { display: flex; align-items: center; gap: 16px; }
.circ { --p: 0; --c: var(--gold); width: 78px; height: 78px; border-radius: 50%; flex: none; position: relative; background: conic-gradient(var(--c) calc(var(--p) * 1%), rgba(255, 255, 255, 0.07) 0); display: grid; place-items: center; }
.circ::after { content: ''; position: absolute; inset: 8px; border-radius: 50%; background: #0a0a0a; }
.cv { position: relative; z-index: 1; font-size: 17px; font-weight: 800; }
.conv-side { flex: 1; }
.barline { display: flex; align-items: center; gap: 9px; margin-bottom: 9px; }
.bk { font-size: 10.5px; font-weight: 700; color: var(--w55); width: 74px; flex: none; }
.track { flex: 1; height: 7px; border-radius: 99px; background: rgba(255, 255, 255, 0.06); overflow: hidden; }
.track i { display: block; height: 100%; border-radius: 99px; background: linear-gradient(90deg, var(--gold), var(--gold-d)); transition: width 0.9s cubic-bezier(0.2, 0.8, 0.2, 1); }
.track i.g { background: linear-gradient(90deg, var(--g), var(--g2)); } .track i.b { background: linear-gradient(90deg, #60a5fa, #1877f2); }
.bn { font-size: 11px; font-weight: 800; width: 42px; text-align: right; flex: none; }
.topch { margin-top: 10px; font-size: 10.5px; color: var(--w55); }
.lb { padding: 6px 8px; }
.lbhead, .lbrow { display: grid; grid-template-columns: 40px 44px 1fr 130px 130px 70px; align-items: center; gap: 12px; }
.lbhead { padding: 8px 14px 4px; }
.lbrow { padding: 12px 14px; border-radius: 12px; transition: 0.16s; }
.lbrow:hover { background: rgba(251, 191, 36, 0.05); }
.lbrow + .lbrow { border-top: 1px solid rgba(255, 255, 255, 0.04); }
.lbrank { font-size: 15px; font-weight: 800; color: var(--w40); text-align: center; }
.lbrank.t1 { color: var(--gold); } .lbrank.t2 { color: #cbd5e1; } .lbrank.t3 { color: #d97706; }
.av { width: 38px; height: 38px; border-radius: 11px; display: grid; place-items: center; font-size: 13px; font-weight: 800; background: linear-gradient(135deg, #475569, #334155); color: #cbd5e1; position: relative; }
/* Presencia real (Victor 2026-06-09): punto pulsante 🟢 conectado · 🟡 ocupado · ⚪ desconectado */
.presdot { position: absolute; bottom: -2px; right: -2px; width: 11px; height: 11px; border-radius: 50%; border: 2px solid #060607; background: #44454a; }
.pres-online .presdot { background: #34c759; animation: presPulseG 1.6s infinite; }
.pres-busy .presdot { background: #e0a23c; animation: presPulseA 1.8s infinite; }
@keyframes presPulseG { 0% { box-shadow: 0 0 0 0 rgba(52,199,89,.55); } 70% { box-shadow: 0 0 0 7px rgba(52,199,89,0); } 100% { box-shadow: 0 0 0 0 rgba(52,199,89,0); } }
@keyframes presPulseA { 0% { box-shadow: 0 0 0 0 rgba(224,162,60,.5); } 70% { box-shadow: 0 0 0 6px rgba(224,162,60,0); } 100% { box-shadow: 0 0 0 0 rgba(224,162,60,0); } }
.ln { font-size: 13px; font-weight: 800; } .ls { font-size: 10px; color: var(--w40); font-weight: 600; margin-top: 2px; }
.ls-v { font-size: 13.5px; font-weight: 800; } .ls-l { font-size: 9px; color: var(--w40); text-transform: uppercase; letter-spacing: 0.5px; font-weight: 700; } .dim { color: var(--w40); font-size: 11px; }
.lbscore { font-size: 18px; font-weight: 800; color: var(--gold); text-align: right; }
.loading, .empty { padding: 40px; text-align: center; color: var(--w55); font-size: 13px; font-weight: 600; }
.empty.card { margin-top: 4px; }
.legend { padding: 14px 18px; margin-bottom: 16px; display: flex; gap: 18px; flex-wrap: wrap; align-items: center; }
.legend .sec-title { margin-right: auto; }
.grade { font-size: 9px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.8px; padding: 4px 9px; border-radius: 99px; }
.g-exc { color: var(--g); background: rgba(34, 197, 94, 0.14); border: 1px solid rgba(34, 197, 94, 0.4); }
.g-bue { color: var(--gold); background: rgba(251, 191, 36, 0.14); border: 1px solid rgba(251, 191, 36, 0.4); }
.g-reg { color: #cbd5e1; background: rgba(255, 255, 255, 0.08); border: 1px solid rgba(255, 255, 255, 0.18); }
.g-rie { color: var(--r); background: rgba(239, 68, 68, 0.14); border: 1px solid rgba(239, 68, 68, 0.4); }
/* Tarjetas fluidas (Propuesta 1, Victor 2026-06-06): reacomodan solas 3->2->1
   columnas segun el ancho (PC, laptop, celular) sin saltos bruscos. */
.pfgrid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 14px; }
.pf { padding: 17px 18px; transition: 0.2s; }
.pf:hover { transform: translateY(-3px); border-color: rgba(251, 191, 36, 0.25); }
.pfh { display: flex; align-items: center; gap: 11px; margin-bottom: 14px; }
.pfico { width: 38px; height: 38px; border-radius: 11px; display: grid; place-items: center; font-size: 18px; font-weight: 800; flex: none; color: #fff; }
.pfico.ig { background: linear-gradient(135deg, #f58529, #dd2a7b 50%, #8134af); }
.pfico.fb { background: linear-gradient(135deg, #1877f2, #0c4eb8); font-family: Georgia, serif; font-style: italic; }
.pfico.ms { background: linear-gradient(135deg, #0084ff, #a033ff); }
.pfico.tk { background: linear-gradient(135deg, #25f4ee, #000 55%, #fe2c55); }
.pfico.wa { background: linear-gradient(135deg, #25d366, #128c7e); }
.pfico.web { background: linear-gradient(135deg, #475569, #334155); color: #cbd5e1; }
.pfn { font-size: 14px; font-weight: 800; } .pft { font-size: 10px; color: var(--w40); font-weight: 600; }
.growth { margin-left: auto; font-size: 10px; font-weight: 800; padding: 2px 7px; border-radius: 6px; }
.kdn { color: var(--r); background: rgba(239, 68, 68, 0.12); }
.pfstats { display: grid; grid-template-columns: repeat(auto-fit, minmax(70px, 1fr)); gap: 9px; margin-bottom: 12px; }
.pfs { text-align: center; background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 9px; padding: 8px 4px; }
.pfs .v { font-size: 16px; font-weight: 800; } .pfs .v.g { color: var(--g); }
.pfs .l { font-size: 8.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; color: var(--w40); margin-top: 2px; }
.sumrow { margin-top: 16px; padding: 18px 20px; display: flex; gap: 22px; flex-wrap: wrap; justify-content: space-around; }
.sumitem { text-align: center; }
.suml { font-size: 11px; color: var(--w40); font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }
.sumv { font-size: 16px; font-weight: 800; margin-top: 5px; }
.sumv .g { color: var(--g); } .sumv .gold { color: var(--gold); } .sumv .red { color: var(--r); }
.rtgrid { display: grid; grid-template-columns: 1.4fr 1fr; gap: 14px; }
.feed { padding: 8px; max-height: 440px; overflow: auto; }
.feedrow { display: flex; align-items: center; gap: 12px; padding: 11px 13px; border-radius: 11px; transition: 0.16s; }
.feedrow:hover { background: rgba(255, 255, 255, 0.03); }
.feedrow + .feedrow { border-top: 1px solid rgba(255, 255, 255, 0.04); }
.fdot { width: 8px; height: 8px; border-radius: 50%; flex: none; background: var(--g); box-shadow: 0 0 8px var(--g); animation: pulse 1.6s infinite; }
.fdot.b { background: #60a5fa; box-shadow: 0 0 8px #60a5fa; }
.ftxt { flex: 1; font-size: 12px; } .ftxt b { font-weight: 800; }
.ftime { font-size: 10px; color: var(--w40); font-weight: 700; }
.alert { display: flex; gap: 11px; padding: 13px 14px; border-radius: 12px; margin-bottom: 10px; align-items: flex-start; background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.07); }
.alert.warn { border-color: rgba(251, 191, 36, 0.3); background: rgba(251, 191, 36, 0.05); }
.alert.danger { border-color: rgba(239, 68, 68, 0.3); background: rgba(239, 68, 68, 0.05); }
.alert.good { border-color: rgba(34, 197, 94, 0.3); background: rgba(34, 197, 94, 0.05); }
.alert .aico { font-size: 17px; } .alert .atxt { font-size: 12px; font-weight: 600; line-height: 1.4; }
.soon { padding: 60px 40px; text-align: center; }
.soon-ico { font-size: 40px; } .soon-t { font-size: 18px; font-weight: 800; margin-top: 12px; } .soon-d { font-size: 12.5px; color: var(--w55); margin-top: 8px; max-width: 440px; margin-inline: auto; line-height: 1.6; }

/* ── Selector de período ── */
.rangepill { cursor: default; } .rangepill:hover { transform: none; background: rgba(255, 255, 255, 0.06); border-color: rgba(255, 255, 255, 0.12); color: #fff; }
.period { display: inline-flex; gap: 3px; background: rgba(255, 255, 255, 0.04); border: 1px solid var(--bd); border-radius: 12px; padding: 4px; flex-wrap: wrap; }
.pseg { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 700; color: var(--w55); padding: 8px 15px; border-radius: 9px; cursor: pointer; transition: 0.16s; white-space: nowrap; user-select: none; }
.pseg:hover { color: #fff; }
.pseg .ic { font-size: 12px; opacity: 0.7; }
.pseg.on { background: linear-gradient(135deg, var(--gold), var(--gold-d)); color: #000; box-shadow: 0 0 14px rgba(251, 191, 36, 0.3); }
.pseg.on .ic { opacity: 1; }
.range-panel { margin-top: 12px; display: flex; align-items: flex-end; gap: 16px; flex-wrap: wrap; max-width: 720px; padding: 16px 18px; border: 1px solid rgba(251, 191, 36, 0.3); border-radius: 14px; background: linear-gradient(180deg, rgba(251, 191, 36, 0.05), transparent); }
.rlbl { font-size: 9.5px; font-weight: 800; text-transform: uppercase; letter-spacing: 1px; color: var(--w40); margin-bottom: 6px; }
.date-box { background: var(--card); border: 1px solid var(--bd); border-radius: 9px; color: #fff; font-family: inherit; font-size: 13px; font-weight: 700; padding: 9px 12px; color-scheme: dark; }
.date-box:focus { outline: none; border-color: rgba(251, 191, 36, 0.5); }
.range-arrow { color: var(--w40); font-size: 15px; padding-bottom: 9px; }
.range-go { margin-left: auto; border: none; cursor: pointer; font-family: inherit; font-weight: 800; font-size: 12px; color: #000; background: linear-gradient(135deg, var(--gold), var(--gold-d)); border-radius: 9px; padding: 11px 18px; box-shadow: 0 0 14px rgba(251, 191, 36, 0.3); }
.range-hint { flex-basis: 100%; font-size: 10.5px; color: var(--w40); font-weight: 600; }
.rangetag { font-size: 10px; font-weight: 800; color: var(--gold); background: rgba(251, 191, 36, 0.1); border: 1px solid rgba(251, 191, 36, 0.3); border-radius: 99px; padding: 4px 11px; }

/* ── Tabla por agente ── */
.tblcard { padding: 4px 6px; }
.tbl { width: 100%; border-collapse: collapse; }
.tbl thead th { font-size: 9.5px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.8px; color: var(--w40); text-align: right; padding: 12px 14px; white-space: nowrap; }
.tbl thead th.l { text-align: left; } .tbl thead th.rgt { text-align: right; }
.tbl thead th.hi { color: var(--gold); }
.tbl tbody tr { border-top: 1px solid rgba(255, 255, 255, 0.05); transition: 0.16s; }
.tbl tbody tr:hover { background: rgba(251, 191, 36, 0.05); }
.agent-row { cursor: pointer; }
.tbl td { padding: 13px 14px; text-align: right; vertical-align: middle; white-space: nowrap; font-size: 12.5px; font-weight: 700; color: var(--w55); }
.tbl td.l { text-align: left; } .tbl td.rgt { text-align: right; }
.agc { display: flex; align-items: center; gap: 11px; }
.av.sm { width: 34px; height: 34px; border-radius: 10px; font-size: 12px; }
.an { font-size: 13px; font-weight: 800; color: #fff; }
.ar { font-size: 10px; color: var(--w40); font-weight: 600; margin-top: 2px; }
.tbl .big { font-size: 16px; font-weight: 800; letter-spacing: -0.5px; color: #fff; }
.tbl .big.gold { color: var(--gold); } .tbl .big.red { color: var(--r); } .tbl .big.dim { color: var(--w40); }
.tcclose { white-space: nowrap; }
.minibar { display: inline-block; width: 44px; height: 5px; border-radius: 99px; background: rgba(255, 255, 255, 0.08); margin-left: 8px; vertical-align: middle; overflow: hidden; position: relative; }
.minibar i { position: absolute; left: 0; top: 0; bottom: 0; border-radius: 99px; background: linear-gradient(90deg, var(--gold), var(--gold-d)); }

/* Eficiencia del Equipo · ventas + dinero + tiempo (Victor 2026-06-07) */
.knum.gold { color: var(--gold); }
.tbl .big.g { color: var(--g); }
.moneycell { white-space: nowrap; }
.moneycell .mv { font-weight: 800; color: var(--gold); font-variant-numeric: tabular-nums; }
.moneycell .mbar { display: inline-block; width: 110px; height: 7px; border-radius: 99px; background: rgba(255, 255, 255, 0.06); margin-left: 10px; vertical-align: middle; overflow: hidden; position: relative; }
.moneycell .mbar i { position: absolute; left: 0; top: 0; bottom: 0; border-radius: 99px; background: linear-gradient(90deg, rgba(251, 191, 36, 0.45), var(--gold)); }
.rtcell { display: inline-flex; align-items: center; gap: 7px; font-weight: 700; font-variant-numeric: tabular-nums; }
.rtcell .rtdot { width: 8px; height: 8px; border-radius: 50%; flex: none; }
.rt-f { color: #86e0ad; } .rt-f .rtdot { background: var(--g); }
.rt-m { color: #f0d68a; } .rt-m .rtdot { background: var(--gold); }
.rt-s { color: #f3a3a3; } .rt-s .rtdot { background: var(--r); }
.agent-row.topseller { background: linear-gradient(90deg, rgba(251, 191, 36, 0.07), transparent 60%); }
.agent-row.topseller td:first-child { box-shadow: inset 3px 0 0 var(--gold); }
/* Cada métrica es clickeable → abre el modal con esos clientes (Victor 2026-06-07) */
.tbl td.mcell { cursor: pointer; transition: background 0.12s; }
.tbl td.mcell:hover { background: rgba(201, 163, 90, 0.08); box-shadow: inset 0 0 0 1px rgba(201, 163, 90, 0.3); }

/* Matriz Clientes Nuevos × Sucursal × Canal (P2 monocromo · Victor 2026-05-31) */
.ncm-card { margin-top: 18px; padding: 0; overflow: hidden; }
.ncm-head {
  padding: 18px 22px 16px;
  display: flex; align-items: flex-start; justify-content: space-between; gap: 18px;
  border-bottom: 1px solid rgba(255,255,255,0.05);
}
.ncm-title {
  font-size: 10px; letter-spacing: 0.18em; font-weight: 600;
  color: var(--gold); margin-bottom: 4px;
}
.ncm-sub { font-size: 11px; color: rgba(255,255,255,0.45); }
.ncm-total {
  display: flex; align-items: baseline; gap: 8px;
  font-variant-numeric: tabular-nums;
}
.ncm-total-label { font-size: 11px; color: rgba(255,255,255,0.55); }
.ncm-total-value { font-size: 18px; font-weight: 700; color: #fff; }
.ncm-delta { font-size: 10px; font-weight: 600; margin-left: 4px; }
.ncm-up { color: #7fb088; }
.ncm-down { color: #d4a8a8; }

.ncm-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.ncm-table thead tr {
  border-bottom: 1px solid rgba(255,255,255,0.05);
}
.ncm-table th {
  padding: 12px 14px; font-weight: 600;
  font-size: 10px; letter-spacing: 0.12em;
  color: rgba(255,255,255,0.40);
}
.ncm-table th.left { text-align: left; padding-left: 22px; }
.ncm-table th.right { text-align: right; }
.ncm-table th.gold-th { color: var(--gold); padding-right: 22px; }
.ncm-table td { padding: 11px 14px; border-top: 1px solid rgba(255,255,255,0.04); }
.ncm-table td.left { text-align: left; padding-left: 22px; color: #fff; }
.ncm-table td.right { text-align: right; }
.ncm-table td.num { font-variant-numeric: tabular-nums; color: rgba(255,255,255,0.85); }
.ncm-table td.dim { color: rgba(255,255,255,0.20); }
.ncm-row { transition: background 0.12s ease; }
.ncm-row:hover { background: rgba(255,255,255,0.02); }
.ncm-code {
  font-size: 10px; color: rgba(255,255,255,0.35);
  margin-left: 6px; letter-spacing: 0.05em;
}
.ncm-row-total {
  color: var(--gold) !important; font-weight: 700;
  padding-right: 22px !important;
}
.ncm-total-row {
  background: rgba(201,163,90,0.04);
  border-top: 1px solid rgba(201,163,90,0.18);
}
.ncm-total-row td { border-top: 1px solid rgba(201,163,90,0.18); padding-top: 14px; padding-bottom: 14px; }
.ncm-total-label-cell {
  color: var(--gold) !important;
  font-size: 11px !important;
  letter-spacing: 0.15em;
  font-weight: 700;
}
.ncm-grand-total {
  color: var(--gold) !important; font-size: 15px !important;
  font-weight: 700; padding-right: 22px !important;
}

/* ── Clientes en Vivo por Sucursal (P5 · Victor 2026-06-03) ── */
.live-ctrl { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; margin-bottom: 16px; }
.seg { display: inline-flex; gap: 3px; background: rgba(255, 255, 255, 0.04); border: 1px solid var(--bd); border-radius: 12px; padding: 4px; }
.segbtn { font-size: 12px; font-weight: 800; color: var(--w55); padding: 8px 16px; border-radius: 9px; cursor: pointer; transition: 0.16s; user-select: none; }
.segbtn:hover { color: #fff; }
.segbtn.on { background: linear-gradient(135deg, var(--gold), var(--gold-d)); color: #000; }
.seg.day .segbtn.on { background: linear-gradient(135deg, #60a5fa, #1877f2); color: #fff; }
/* Inicio de sesión por sucursal (Victor 2026-06-09) */
.lg-bhead { display: flex; align-items: center; justify-content: space-between; padding: 13px 16px; border-bottom: 1px solid var(--bd); gap: 12px; flex-wrap: wrap; }
.lg-bname { font-size: 12.5px; letter-spacing: 1.2px; font-weight: 800; color: #d6d4cf; display: flex; align-items: center; gap: 9px; }
.lg-bsum { display: flex; align-items: center; gap: 14px; font-size: 11.5px; }
.lg-cnt { color: var(--w55); }
.lg-g { color: var(--g); font-weight: 700; }
.lg-r { color: var(--r); font-weight: 700; }
.lg-r.dimr { color: var(--w55); font-weight: 600; }
.lg-pr { color: var(--gold); font-weight: 700; }
/* Acordeón Eficiencia del Equipo por sucursal (Victor 2026-06-09) */
.bacc { margin-bottom: 10px; }
.bacc .lg-bhead { border-bottom: none; cursor: pointer; transition: background 0.16s; user-select: none; }
.bacc.open .lg-bhead { border-bottom: 1px solid var(--bd); }
.bacc .lg-bhead.bclick:hover { background: rgba(251, 191, 36, 0.05); }
.chev { color: var(--gold); font-size: 11px; display: inline-block; transition: transform 0.22s cubic-bezier(0.2, 0.8, 0.2, 1); }
.bacc.open .chev { transform: rotate(90deg); }
.lg-d { color: var(--gold); font-weight: 800; font-variant-numeric: tabular-nums; }
.bbody { padding: 4px 6px; }
.bempty { color: var(--w40); font-style: italic; text-align: center; padding: 16px; }
.lg-days { font-size: 11px; color: var(--w55); margin-left: 6px; }
.lg-pill { font-size: 11px; font-weight: 700; padding: 3px 11px; border-radius: 999px; white-space: nowrap; }
.lg-ontime { color: var(--g); background: rgba(34, 197, 94, 0.12); border: 1px solid rgba(34, 197, 94, 0.3); }
.lg-late { color: var(--r); background: rgba(239, 68, 68, 0.12); border: 1px solid rgba(239, 68, 68, 0.3); }
.lg-missing { color: var(--w55); background: rgba(255, 255, 255, 0.04); border: 1px solid var(--bd); }
.verdict { display: flex; align-items: center; gap: 14px; padding: 15px 18px; border-radius: 14px; margin-bottom: 18px; background: linear-gradient(120deg, rgba(251, 191, 36, 0.1), rgba(251, 191, 36, 0.02)); border: 1px solid rgba(251, 191, 36, 0.28); }
.vem { font-size: 22px; flex: none; }
.vtx { font-size: 13.5px; font-weight: 700; } .vtx b { color: var(--gold); }
.vsub { display: block; color: var(--w55); font-weight: 600; font-size: 12px; margin-top: 3px; }
.live-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
.bcard.win { border-color: rgba(251, 191, 36, 0.5); box-shadow: 0 0 0 1px rgba(251, 191, 36, 0.22) inset, 0 14px 36px rgba(0, 0, 0, 0.55); }
.bname-lg { font-size: 13px; font-weight: 800; }
.crown { margin-left: auto; font-size: 15px; }
.live-big { font-size: 40px; font-weight: 800; letter-spacing: -1.5px; line-height: 1; } .live-big.gold { color: var(--gold); }
.live-cap { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: var(--w40); margin-top: 5px; }
.live-cmp { font-size: 11.5px; font-weight: 700; color: var(--w55); margin-top: 9px; } .live-cmp b { color: #fff; }
.live-cmp .up { color: var(--g); margin-left: 5px; } .live-cmp .dn { color: var(--r); margin-left: 5px; }
.live-mini { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-top: 14px; padding-top: 12px; border-top: 1px solid rgba(255, 255, 255, 0.06); }
.lm-v { font-size: 15px; font-weight: 800; } .lm-v.u { color: var(--gold); } .lm-v.m { color: #60a5fa; }
.lm-l { font-size: 8.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; color: var(--w40); margin-top: 2px; }
.live-note { font-size: 10.5px; color: var(--w40); font-weight: 600; margin-top: 18px; line-height: 1.5; }

/* ===== Celular (Victor 2026-06-02): el informe se adapta a la pantalla =====
   Las rejillas de escritorio (4/2 columnas) se reducen, las tarjetas de
   plataforma se apilan a ancho completo y las tablas (matriz + leaderboard)
   se vuelven desplazables horizontalmente para no cortar columnas. */
@media (max-width: 767px) {
  .kgrid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
  .bgrid { grid-template-columns: 1fr; }
  .live-grid { grid-template-columns: repeat(2, 1fr); }
  .rtgrid { grid-template-columns: 1fr; }
  .pfgrid { grid-template-columns: 1fr; }
  .pfstats { grid-template-columns: repeat(2, 1fr); gap: 8px; }

  /* Tarjetas de resumen y filtros: que no se desborden */
  .sumrow { gap: 14px; }
  .range-panel { max-width: 100%; }

  /* Matriz Clientes Nuevos: scroll horizontal en vez de cortar TIKTOK/TOTAL */
  .ncm-card { overflow-x: auto; -webkit-overflow-scrolling: touch; }
  .ncm-table { min-width: 480px; }

  /* Leaderboard (Eficiencia del Equipo): columnas compactas para que quepan */
  .lbhead, .lbrow {
    grid-template-columns: 22px 30px minmax(0, 1fr) 56px 56px 40px;
    gap: 7px;
    font-size: 11px;
  }
  .av { width: 30px; height: 30px; border-radius: 9px; font-size: 11px; }

  /* Tabla de Eficiencia del Equipo (9 columnas): scroll horizontal */
  .tblcard { overflow-x: auto; -webkit-overflow-scrolling: touch; }
  .tblcard .tbl { min-width: 720px; }
}
</style>
